import {
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonBadge,
  IonButton,
  IonIcon,
} from "@ionic/react";
import { chevronBack, chevronForward } from "ionicons/icons";
import { CircularProgress, Divider, TextField, Typography } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import Operarios from "src/services/operarios";
import RegistroCapacitacionAPI, { RolCapacitacion, etiquetaTipo } from "src/services/registroCapacitacion";

interface OperarioItem {
  legajo: string;
  nombre: string;
  cargo: string;
}

interface RegistroCapacitacion {
  Id: number;
  IdCaso: string;
  FechaCapa: string;
  Descripcion: string;
  NombreUbicacion: string;
  Capacitador: string;
  Comentario: string;
  EvaluacionRealizada: boolean | null;
  Origen: string;
  Categoria: string;
  Tipo?: string;
  Puntaje?: number | null;
}

interface RegistroReciente {
  Id: number;
  IdCaso: string;
  FechaCapa: string;
  Descripcion: string;
  NombreUbicacion: string;
  NombreEmpleado: string;
  Legajo: number;
  Comentario: string;
  EvaluacionRealizada: boolean | null;
  Origen: string;
  Categoria: string;
  Tipo?: string;
  Puntaje: number | null;
}

interface Props {
  cargo?: string;
}

const PAGE_SIZE = 10;

const formatFecha = (iso: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// "Seguridad · Prevención y respuesta ante emergencias"
const tipoYCategoria = (tipo?: string, categoria?: string) =>
  [etiquetaTipo(tipo), categoria].filter(Boolean).join(" · ");

const colorPuntaje = (p: number) => (p >= 60 ? "success" : "danger");

const PuntajeBadge: React.FC<{ puntaje: number | null | undefined; evaluado: boolean | null }> = ({ puntaje, evaluado }) => {
  if (evaluado !== true || puntaje === null || puntaje === undefined) return null;
  const valor = Math.round(puntaje);
  return <IonBadge color={colorPuntaje(valor)}>{valor}%</IonBadge>;
};

const EstadoBadge: React.FC<{ evaluado: boolean | null }> = ({ evaluado }) => {
  if (evaluado === true) return <IonBadge color="success">Evaluado</IonBadge>;
  if (evaluado === false) return <IonBadge color="warning">Pendiente</IonBadge>;
  return <IonBadge color="medium">—</IonBadge>;
};

const OrigenBadge: React.FC<{ origen: string }> = ({ origen }) => {
  if (!origen) return null;
  const color =
    origen === "App"
      ? "primary"
      : origen.toLowerCase() === "bizagi"
      ? "success"
      : "medium";
  return <IonBadge color={color}>{origen}</IonBadge>;
};

const Paginacion: React.FC<{ pagina: number; total: number; onChange: (p: number) => void }> = ({
  pagina,
  total,
  onChange,
}) => {
  if (total <= 1) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        marginTop: 16,
        paddingBottom: 8,
      }}
    >
      <IonButton fill="outline" size="small" disabled={pagina <= 1} onClick={() => onChange(pagina - 1)}>
        <IonIcon slot="icon-only" icon={chevronBack} />
      </IonButton>
      <Typography variant="body2" style={{ minWidth: 80, textAlign: "center" }}>
        Página {pagina} de {total}
      </Typography>
      <IonButton fill="outline" size="small" disabled={pagina >= total} onClick={() => onChange(pagina + 1)}>
        <IonIcon slot="icon-only" icon={chevronForward} />
      </IonButton>
    </div>
  );
};

const HistorialCapacitacion: React.FC<Props> = ({ cargo }) => {
  const cargoUpper = (cargo || "").toUpperCase();
  // Quien registra (capacitador técnico o admin de Seguridad) ve el puntaje y
  // sus capacitaciones recientes; los fiscales solo consultan.
  const [rol, setRol] = useState<RolCapacitacion | null>(null);
  useEffect(() => {
    RegistroCapacitacionAPI.miRol().then(setRol);
  }, []);
  const esCapacitador =
    cargoUpper.includes("CAPACITADOR TECNICO") ||
    cargoUpper.includes("COORDINADOR DE CAPACITACIONES TECNICAS") ||
    Boolean(rol?.tipos_registro.length);
  // Seguridad capacita a cualquier funcionario: la búsqueda abarca a todos.
  const buscaTodos = Boolean(rol?.tipos_registro.includes("SEGURIDAD"));

  // Búsqueda por operario
  const [operarioInput, setOperarioInput] = useState("");
  const [operarioOpciones, setOperarioOpciones] = useState<OperarioItem[]>([]);
  const [operarioCargando, setOperarioCargando] = useState(false);
  const [operarioSeleccionado, setOperarioSeleccionado] = useState<OperarioItem | null>(null);

  const [registros, setRegistros] = useState<RegistroCapacitacion[]>([]);
  const [cargando, setCargando] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [pagina, setPagina] = useState(1);

  // Recientes del capacitador
  const [recientes, setRecientes] = useState<RegistroReciente[]>([]);
  const [recientesCargando, setRecientesCargando] = useState(false);
  const [paginaRecientes, setPaginaRecientes] = useState(1);

  // Cargar recientes al montar (solo capacitadores)
  useEffect(() => {
    if (!esCapacitador) return;
    let activo = true;
    const cargar = async () => {
      setRecientesCargando(true);
      try {
        const res = await RegistroCapacitacionAPI.historialRecientes();
        if (!activo) return;
        setRecientes(res.data?.datos || []);
      } catch {
        if (!activo) return;
        setRecientes([]);
      } finally {
        if (!activo) return;
        setRecientesCargando(false);
      }
    };
    cargar();
    return () => { activo = false; };
  }, [esCapacitador]);

  // Búsqueda de operarios con debounce
  useEffect(() => {
    const texto = operarioInput.trim();
    if (texto.length < (buscaTodos && !/^\d+$/.test(texto) ? 3 : 2)) {
      setOperarioOpciones([]);
      return;
    }
    let activo = true;
    setOperarioCargando(true);
    const timer = setTimeout(async () => {
      try {
        const arr: any[] = buscaTodos
          ? (await RegistroCapacitacionAPI.buscarFuncionarios(texto)).filter((f) => f.legajo)
          : await Operarios.listarOperariosSupervisores(texto, true);
        if (!activo) return;
        setOperarioOpciones(
          arr.map((o: any) => ({
            legajo: String(o.legajo ?? ""),
            nombre: String(o.nombre ?? ""),
            cargo: String(o.cargo ?? ""),
          }))
        );
      } catch {
        if (!activo) return;
        setOperarioOpciones([]);
      } finally {
        if (!activo) return;
        setOperarioCargando(false);
      }
    }, 350);
    return () => { activo = false; clearTimeout(timer); };
  }, [operarioInput, buscaTodos]);

  useEffect(() => {
    setPagina(1);
    if (!operarioSeleccionado) {
      setRegistros([]);
      setBuscado(false);
      return;
    }
    const cargar = async () => {
      setCargando(true);
      setBuscado(false);
      try {
        const res = await RegistroCapacitacionAPI.historial(operarioSeleccionado.legajo);
        setRegistros(res.data?.datos || []);
      } catch {
        setRegistros([]);
      } finally {
        setCargando(false);
        setBuscado(true);
      }
    };
    cargar();
  }, [operarioSeleccionado]);

  const recientesPaginadas = useMemo(() => {
    const totalPaginas = Math.max(1, Math.ceil(recientes.length / PAGE_SIZE));
    const paginaActual = Math.min(paginaRecientes, totalPaginas);
    const inicio = (paginaActual - 1) * PAGE_SIZE;
    const fin = inicio + PAGE_SIZE;
    return {
      items: recientes.slice(inicio, fin),
      totalPaginas,
      paginaActual,
      inicio,
      fin: Math.min(fin, recientes.length),
    };
  }, [recientes, paginaRecientes]);

  const operarioPaginado = useMemo(() => {
    const totalPaginas = Math.max(1, Math.ceil(registros.length / PAGE_SIZE));
    const paginaActual = Math.min(pagina, totalPaginas);
    const inicio = (paginaActual - 1) * PAGE_SIZE;
    const fin = inicio + PAGE_SIZE;
    return {
      items: registros.slice(inicio, fin),
      totalPaginas,
      paginaActual,
      inicio,
      fin: Math.min(fin, registros.length),
    };
  }, [registros, pagina]);

  return (
    <IonPage>
      <Header titulo="Historial de Capacitaciones" />
      <IonContent id="historial-capacitacion">
        <div style={{ padding: 16, maxWidth: 600, margin: "0 auto", boxSizing: "border-box" }}>

          {/* Buscador de operario */}
          <MuiAutocomplete
            style={{ width: "100%", marginBottom: 16 }}
            options={operarioOpciones}
            getOptionLabel={(o) => `${o.nombre} (${o.legajo}) - ${o.cargo}`}
            getOptionSelected={(a, b) => a.legajo === b.legajo}
            value={operarioSeleccionado}
            onChange={(_, val) => setOperarioSeleccionado(val)}
            inputValue={operarioInput}
            onInputChange={(_, val, reason) => {
              if (reason !== "reset" || val !== "") setOperarioInput(val);
            }}
            loading={operarioCargando}
            loadingText="Buscando..."
            noOptionsText={operarioInput.trim().length < 2 ? "Escriba al menos 2 caracteres" : "Sin coincidencias"}
            filterOptions={(opts) => opts}
            renderInput={(params) => (
              <TextField
                {...params}
                label={buscaTodos ? "Funcionario (nombre, cédula o legajo)" : "Operario / Supervisor"}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {operarioCargando ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Resultados de búsqueda */}
          {cargando && (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          )}

          {!cargando && buscado && registros.length === 0 && operarioSeleccionado && (
            <Typography color="textSecondary" style={{ textAlign: "center", marginTop: 24 }}>
              No se encontraron capacitaciones para este operario.
            </Typography>
          )}

          {!cargando && registros.length > 0 && (
            <>
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: "block" }}>
                {registros.length} registro{registros.length !== 1 ? "s" : ""} (últimos 6 meses) — mostrando {operarioPaginado.inicio + 1}–{operarioPaginado.fin}
              </Typography>
              <IonList style={{ margin: "0 -16px" }}>
                {operarioPaginado.items.map((r) => (
                  <IonItem key={r.Id} lines="full">
                    <IonLabel style={{ whiteSpace: "normal" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
                            {r.Descripcion || "—"}
                          </p>
                          {(() => {
                            const esBizagi = r.Origen.toLowerCase() === "bizagi";
                            const idMostrar = esBizagi ? r.IdCaso : String(r.Id);
                            const labelId = esBizagi ? "Caso" : "ID";
                            return idMostrar ? (
                              <p style={{ margin: "2px 0 0", fontSize: 11, color: "#999", fontFamily: "monospace" }}>
                                {labelId}: {idMostrar}
                              </p>
                            ) : null;
                          })()}
                          {tipoYCategoria(r.Tipo, r.Categoria) ? (
                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#888" }}>
                              {tipoYCategoria(r.Tipo, r.Categoria)}
                            </p>
                          ) : null}
                          <p style={{ margin: "4px 0 0", fontSize: 13 }}>📅 {formatFecha(r.FechaCapa)}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 13 }}>📍 {r.NombreUbicacion || "—"}</p>
                          {r.Capacitador ? (
                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#555" }}>
                              Capacitador: {r.Capacitador}
                            </p>
                          ) : null}
                          {r.Comentario ? (
                            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#777", fontStyle: "italic" }}>
                              {r.Comentario}
                            </p>
                          ) : null}
                        </div>
                        <div style={{ flexShrink: 0, marginTop: 2, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <OrigenBadge origen={r.Origen} />
                          <EstadoBadge evaluado={r.EvaluacionRealizada} />
                          {esCapacitador && <PuntajeBadge puntaje={r.Puntaje} evaluado={r.EvaluacionRealizada} />}
                        </div>
                      </div>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
              <Paginacion pagina={operarioPaginado.paginaActual} total={operarioPaginado.totalPaginas} onChange={setPagina} />
            </>
          )}

          {/* Recientes del capacitador (solo si no hay búsqueda activa) */}
          {esCapacitador && !operarioSeleccionado && (
            <>
              <Divider style={{ margin: "16px 0 12px" }} />
              <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
                Mis capacitaciones recientes
              </Typography>

              {recientesCargando && (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                  <CircularProgress />
                </div>
              )}

              {!recientesCargando && recientes.length === 0 && (
                <Typography color="textSecondary" style={{ textAlign: "center", marginTop: 16 }}>
                  Aún no registraste capacitaciones.
                </Typography>
              )}

              {!recientesCargando && recientes.length > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: "block" }}>
                    {recientes.length} registro{recientes.length !== 1 ? "s" : ""} (últimos 6 meses) — mostrando {recientesPaginadas.inicio + 1}–{recientesPaginadas.fin}
                  </Typography>
                  <IonList style={{ margin: "0 -16px" }}>
                    {recientesPaginadas.items.map((r) => (
                      <IonItem key={r.Id} lines="full">
                        <IonLabel style={{ whiteSpace: "normal" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>
                                {r.Descripcion || "—"}
                              </p>
                              {tipoYCategoria(r.Tipo, r.Categoria) ? (
                                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#888" }}>
                                  {tipoYCategoria(r.Tipo, r.Categoria)}
                                </p>
                              ) : null}
                              <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                                👤 {r.NombreEmpleado} ({r.Legajo})
                              </p>
                              <p style={{ margin: "2px 0 0", fontSize: 13 }}>📅 {formatFecha(r.FechaCapa)}</p>
                              <p style={{ margin: "2px 0 0", fontSize: 13 }}>📍 {r.NombreUbicacion || "—"}</p>
                              {r.Comentario ? (
                                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#777", fontStyle: "italic" }}>
                                  {r.Comentario}
                                </p>
                              ) : null}
                            </div>
                            <div style={{ flexShrink: 0, marginTop: 2, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                              <OrigenBadge origen={r.Origen} />
                              <EstadoBadge evaluado={r.EvaluacionRealizada} />
                              <PuntajeBadge puntaje={r.Puntaje} evaluado={r.EvaluacionRealizada} />
                            </div>
                          </div>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                  <Paginacion
                    pagina={recientesPaginadas.paginaActual}
                    total={recientesPaginadas.totalPaginas}
                    onChange={setPaginaRecientes}
                  />
                </>
              )}
            </>
          )}

        </div>
      </IonContent>
    </IonPage>
  );
};

export default HistorialCapacitacion;
